import { useState } from "react";
import { secondsToMinutes } from "date-fns";
import { Box } from "@twilio-paste/core/box";
import { ChatBookend, ChatBookendItem } from "@twilio-paste/core/chat-log";
import { useTwilioSyncMap } from "@twiliointernal/e2-commons-shared-hooks";
import { AvatarWrapper } from "@twiliointernal/e2-commons-shared-ui";

import {
  ChatActivityData,
  TransferType,
  WaitingInLineProps,
} from "./definitions";
import WaitingInLineActions from "./sub/WaitingInLineActions";

import { copy } from "./definitions";
import { NEXT_IN_LINE_QUEUE_POSITION } from "./definitions/constants";
import { MessageBlockRole } from "@twiliointernal/e2-commons-shared-definitions";
import { animated, useSpring } from "@twilio-paste/core/animation-library";

const WaitingInLine = (props: WaitingInLineProps) => {
  const {
    liveChatId: currentLiveChatId,
    token,
    chatActivitySyncMapId,
    transfer,
    actions,
    ingress,
  } = props;

  const [waitStatsLoading, setWaitStatsLoading] = useState<boolean>(true);
  const [waitTimeMinutes, setWaitTimeMinutes] = useState<number | null>(null);
  const [linePosition, setLinePosition] = useState<number | null>(null);
  const isNextInLine = linePosition === NEXT_IN_LINE_QUEUE_POSITION;
  const shouldRenderWaitingInLineActions = !waitStatsLoading && !isNextInLine;

  const springsActions = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 250,
  });
  const AnimatedWrapper = animated(AvatarWrapper);

  useTwilioSyncMap<ChatActivityData>({
    accessToken: token,
    syncMapId: chatActivitySyncMapId,
    onItemUpdated: (event) => {
      const { key: incomingLiveChatId } = event.item;

      if (incomingLiveChatId === currentLiveChatId) {
        updateWaitingData(event.item.data);
      }
    },
    onInitialize: async (map) => {
      try {
        const { data } = await map.get(currentLiveChatId);
        updateWaitingData(data as ChatActivityData);
      } catch (error) {
        console.warn(
          "SyncMap is not ready yet, waiting for the item update event..."
        );
      }
    },
    onError: (error) => {
      console.error("Error syncing chat activity map", error);
    },
    onItemAdded: (event) => {
      const { key: incomingLiveChatId } = event.item;
      if (incomingLiveChatId === currentLiveChatId) {
        updateWaitingData(event.item.data);
      }
    },
  });

  const updateWaitingData = (data: ChatActivityData) => {
    if (!data) {
      return;
    }

    const { taskQueueEstWaitTime, taskQueuePosition } = data;
    setWaitTimeMinutes(secondsToMinutes(taskQueueEstWaitTime));
    setLinePosition(taskQueuePosition);
    setWaitStatsLoading(false);
  };

  const renderWaitingInLineActions = () =>
    shouldRenderWaitingInLineActions ? (
      <AnimatedWrapper style={springsActions} role={MessageBlockRole.Assistant}>
        <WaitingInLineActions
          liveChatId={currentLiveChatId}
          actions={actions}
          ingress={ingress}
        />
      </AnimatedWrapper>
    ) : null;

  const renderEstimatedWaitingInLineText = () => {
    if (waitStatsLoading || waitTimeMinutes === null) {
      return <ChatBookendItem>{renderHeading()}</ChatBookendItem>;
    }

    return (
      <>
        <ChatBookendItem>{renderHeading()}</ChatBookendItem>
        <ChatBookendItem>
          {copy.waitMessages.estimatedMinutes(waitTimeMinutes)}
        </ChatBookendItem>
        {isNextInLine && (
          <ChatBookendItem>{copy.waitMessages.nextInLine}</ChatBookendItem>
        )}
      </>
    );
  };

  const renderHeading = () => {
    if (transfer && transfer.type === TransferType.Queue) {
      return copy.waitMessages.headingForTransferToQueue(transfer.queue);
    }

    return copy.waitMessages.heading;
  };

  return (
    <Box>
      <ChatBookend>{renderEstimatedWaitingInLineText()}</ChatBookend>

      {renderWaitingInLineActions()}
    </Box>
  );
};

export default WaitingInLine;
